import { actionTooltipProps } from "@/ds";
import type { TableDensity } from "@/ds";
import { FINANCEIRO_STATUS_OPTIONS } from "@/modules/financeiro-obras";
import type { FinanceiroObrasSortField } from "@/modules/financeiro-obras";
import type { FinanceiroObrasColumnKey, FinanceiroQuickOption } from "./types-financeiro-obras";

export const FINANCEIRO_LOADING_LABEL = "Carregando obras...";
export const FINANCEIRO_ERROR_TITLE = "Não foi possível carregar as obras.";
export const FINANCEIRO_FORBIDDEN_CARDS_DESCRIPTION =
  "Você não tem permissão para consultar as obras do Financeiro.";
export const FINANCEIRO_NO_VALUE = "-";
export const FINANCEIRO_ROWS_PER_PAGE_LABEL = "Obras por página";
/** Nome base do arquivo exportado (Exportar Visualização). */
export const FINANCEIRO_EXPORT_FILE_NAME = "financeiro-obras-visualizacao";

/** Opções do filtro de Status (painel e filtro rápido): o enum do servidor com a descrição de cada FEL. */
export const STATUS_OPTIONS: FinanceiroQuickOption[] = FINANCEIRO_STATUS_OPTIONS.map((option) => ({
  label: option.descricao,
  value: option.codigo,
}));

/** Opções do filtro de Condição do painel (Figma: Filtro - Condição). */
export const CONDICAO_OPTIONS: FinanceiroQuickOption[] = [
  { label: "Seco", value: "SECO" },
  { label: "Molhado", value: "MOLHADO" },
];

export const SAVED_FILTERS_SCREEN = "financeiro-obras";
export const MOBILE_QUERY = "(max-width: 640px)";
/** Chave da coluna de período do painel; no conjunto salvo ele vira dataInicio/dataFim. */
export const PERIOD_COLUMN = "periodo";
/** Marca que as preferências da tabela já estão no formato do `useTableSettings` do DS. */
export const FINANCEIRO_TABLE_SETTINGS_FORMAT_KEY =
  "hub_web:financeiro-obras-table-settings-format";

/** Tooltip da tag de status (Figma 12884:814227): acima do alvo; o texto vem do `data-pr-tooltip` do alvo. */
const { content: _content, ...tooltipProps } = actionTooltipProps("", { position: "top" });

export const FINANCEIRO_TOOLTIP_PROPS = tooltipProps;

export interface FinanceiroObrasColumnDefinition {
  key: FinanceiroObrasColumnKey;
  label: string;
  /** Rótulo abreviado exibido no cabeçalho (Compacto). */
  header: string;
  /** Rótulo do cabeçalho no mobile, quando difere do desktop. */
  mobileHeader?: string;
  /** Principais: sempre visíveis (Padrões Globais). */
  main: boolean;
  /** Campo de ordenação do contrato; ausente = coluna não ordenável. */
  sortField?: FinanceiroObrasSortField;
  align?: "right";
  /** Tooltip do cabeçalho com o nome completo da sigla (RE, EM, CO, ES, NC). */
  tooltip?: string;
}

/**
 * Dicionário oficial das siglas financeiras (comentário do work item da HU 2670), usado na tooltip do
 * cabeçalho da Lista e dos indicadores dos Cards. CO é "Cotado": o módulo Obras o rotula "% Comprometido",
 * o que está errado para o Financeiro; não copiar.
 */
export const FINANCEIRO_SIGLAS = {
  re: {
    sigla: "RE",
    nome: "Realizado",
    descricao: "Financeiro com nota fiscal classificada pela contabilidade",
  },
  em: {
    sigla: "EM",
    nome: "Empenhado",
    descricao: "Financeiro com pedido de compras aprovado",
  },
  co: {
    sigla: "CO",
    nome: "Cotado",
    descricao: "Financeiro com cotação atualizada de fornecedor/compras",
  },
  nc: {
    sigla: "NC",
    nome: "Não Comprometido",
    descricao: "Financeiro de um escopo macro sem associação a serviços, materiais ou facilidades",
  },
  es: {
    sigla: "ES",
    nome: "Estimado",
    descricao: "Financeiro estimado com base em premissas e histórico",
  },
} as const;

/** Prefixo da tooltip do ícone de condição no Card (Figma 12884:814376: "Condição: Seco"). */
export const FINANCEIRO_CONDICAO_TOOLTIP_PREFIX = "Condição";

/**
 * Colunas da Lista, na ordem do Figma (12760:969941). Principais (sempre visíveis, Padrões Globais): ID,
 * Embarcação, Status, Tipo(s) de Obra, Início, Término e a lupa; Realizado, Outlook, RE, EM, CO, ES, NC,
 * Condição e Duração Total podem ser ocultadas em "Configurações da Tabela". Só as colunas com `sortField`
 * ordenam: as financeiras não, porque o endpoint não suporta.
 */
export const FINANCEIRO_OBRAS_COLUMNS: FinanceiroObrasColumnDefinition[] = [
  { key: "id", label: "ID", header: "ID", main: true, sortField: "id" },
  {
    key: "embarcacao",
    label: "Embarcação",
    header: "Embarcação",
    main: true,
    sortField: "embarcacao",
  },
  {
    key: "statusFinanceiro",
    label: "Status",
    header: "Status",
    main: true,
    sortField: "statusFinanceiro",
  },
  {
    key: "tipoObra",
    label: "Tipo(s) de Obra",
    header: "Tipo(s) de Obra",
    mobileHeader: "Tipo de Obra",
    main: true,
  },
  {
    key: "realizado",
    label: "Realizado BRL M",
    header: "Realizado BRL M",
    main: false,
    align: "right",
  },
  { key: "outlook", label: "Outlook BRL M", header: "Outlook BRL M", main: false, align: "right" },
  {
    key: "re",
    label: FINANCEIRO_SIGLAS.re.sigla,
    header: FINANCEIRO_SIGLAS.re.sigla,
    main: false,
    align: "right",
    tooltip: FINANCEIRO_SIGLAS.re.nome,
  },
  {
    key: "em",
    label: FINANCEIRO_SIGLAS.em.sigla,
    header: FINANCEIRO_SIGLAS.em.sigla,
    main: false,
    align: "right",
    tooltip: FINANCEIRO_SIGLAS.em.nome,
  },
  {
    key: "co",
    label: FINANCEIRO_SIGLAS.co.sigla,
    header: FINANCEIRO_SIGLAS.co.sigla,
    main: false,
    align: "right",
    tooltip: FINANCEIRO_SIGLAS.co.nome,
  },
  {
    key: "es",
    label: FINANCEIRO_SIGLAS.es.sigla,
    header: FINANCEIRO_SIGLAS.es.sigla,
    main: false,
    align: "right",
    tooltip: FINANCEIRO_SIGLAS.es.nome,
  },
  {
    key: "nc",
    label: FINANCEIRO_SIGLAS.nc.sigla,
    header: FINANCEIRO_SIGLAS.nc.sigla,
    main: false,
    align: "right",
    tooltip: FINANCEIRO_SIGLAS.nc.nome,
  },
  { key: "condicao", label: "Condição", header: "Condição", main: false },
  { key: "inicio", label: "Início", header: "Início", main: true, sortField: "inicio" },
  { key: "termino", label: "Término", header: "Término", main: true, sortField: "termino" },
  { key: "duracao", label: "Duração Total", header: "Duração Total", main: false, align: "right" },
  { key: "detalhamento", label: "Detalhamento", header: "", main: true },
];

/** As colunas da HU, na ordem da tela, para o `useTableSettings` do DS (definidas fora do render). */
export const FINANCEIRO_ALL_COLUMNS: readonly FinanceiroObrasColumnKey[] =
  FINANCEIRO_OBRAS_COLUMNS.map((column) => column.key);

/** Colunas principais (definidas na HU): sempre visíveis, travadas no dialog. */
export const FINANCEIRO_MAIN_COLUMNS: readonly FinanceiroObrasColumnKey[] =
  FINANCEIRO_OBRAS_COLUMNS.filter((column) => column.main).map((column) => column.key);

/** Colunas opcionais, para converter as preferências salvas no formato antigo. */
export const FINANCEIRO_OPTIONAL_COLUMNS: string[] = FINANCEIRO_OBRAS_COLUMNS.filter(
  (column) => !column.main,
).map((column) => column.key);

/** Rótulos do dialog "Configuração da Tabela", na ordem das colunas. */
export const FINANCEIRO_COLUMN_LABELS = Object.fromEntries(
  FINANCEIRO_OBRAS_COLUMNS.map((column) => [column.key, column.label]),
) as Record<FinanceiroObrasColumnKey, string>;

/** Larguras do Figma (12760:969941, px ÷ 16), em rem, nas duas densidades. */
export const FINANCEIRO_COLUMN_WIDTHS: Partial<Record<FinanceiroObrasColumnKey, string>> = {
  id: "8.5rem", // sem token no Figma: cabe o código alfanumérico inteiro (ex.: OBR-TST-0001) sem reticências
  embarcacao: "9.6875rem",
  statusFinanceiro: "5rem",
  tipoObra: "9.6875rem",
  realizado: "6.375rem", // 102
  outlook: "6.375rem", // 102
  re: "4.4375rem", // 71
  em: "5.125rem", // 82
  co: "5.0625rem", // 81
  es: "5.125rem", // 82
  nc: "5.0625rem", // 81
  condicao: "6.5rem", // 104
  inicio: "6.0625rem",
  termino: "6.0625rem",
  duracao: "5.3125rem",
};

/** Coluna da lupa: 28 de botão + 12 de cada lado no Confortável e 7 no Compacto. */
export const FINANCEIRO_ACTION_COLUMN_WIDTH: Record<TableDensity, string> = {
  normal: "3.25rem",
  small: "2.625rem",
};

/** Coluna "Obra" do mobile: ID e embarcação juntos, fixa. */
export const FINANCEIRO_MOBILE_OBRA_WIDTH = "9.5rem";

/** Colunas de tags: recuo próprio na célula. */
export const FINANCEIRO_TAGS_COLUMNS: FinanceiroObrasColumnKey[] = ["tipoObra", "statusFinanceiro"];

/** Colunas não fixas que absorvem a sobra de largura em janelas largas. */
export const FINANCEIRO_GROW_COLUMNS: FinanceiroObrasColumnKey[] = ["tipoObra", "statusFinanceiro"];
